import { UserIdSchema } from "@core/domains/identity";
import { identity } from "@core/foundation/function";
import {
  createModel,
  createModelId,
  createModelSchemas,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { OAuthScopesSchema } from "@platform/core/models";

import { OAuthClientIdSchema } from "./oauth-client.ts";
import { ID_PREFIX } from "./shared/id-prefix.ts";

const { generateId: generateOAuthGrantId, idSchema: OAuthGrantIdSchema } =
  createModelId({ idPrefix: ID_PREFIX.OAUTH_GRANT });

export { OAuthGrantIdSchema };

export type OAuthGrantId = z.infer<typeof OAuthGrantIdSchema>;

const OAuthGrantStatusSchema = z.enum(["active", "revoked"]);

export const OAuthGrant = createModel({
  create: (item, schema) => {
    return { ...schema.decode(item), id: generateOAuthGrantId() };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        clientId: OAuthClientIdSchema,
        id: OAuthGrantIdSchema,
        principalId: UserIdSchema,
        refreshTokenDigest: z.string(),
        resource: z.url(),
        scopes: OAuthScopesSchema,
        status: OAuthGrantStatusSchema,
      },
      {
        clientId: "The id of the client the grant consents to.",
        id: "The unique identifier for the grant.",
        principalId: "The id of the user who granted the consent.",
        refreshTokenDigest: "The digest of the grant's current refresh key.",
        resource: "The URL of the resource server the grant covers.",
        scopes: "The scopes the grant allows.",
        status: "The grant's status, either active or revoked.",
      },
    ),
    defaults: { status: "active" },
    derive: {
      create: schema => schema.omit({ id: true }),
      recovery: identity,
      update: schema =>
        schema.pick({ refreshTokenDigest: true, status: true }).partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface OAuthGrant extends z.infer<typeof OAuthGrant.schema> {}
